<template>
    <div :class="cardClass">
        <router-link
            @click="() => setActiveLounge(lounge)"
            class="group w-full h-64 overflow-hidden rounded-lg bg-gray-100 cursor-pointer focus:outline-none hover:opacity-90 active:opacity-90"
            :to="{
                name: 'Lounge',
                params: {
                    id: lounge.id,
                },
            }"
        >
            <ImageLoader class="w-full h-full">
                <template #image>
                    <img
                        loading="lazy"
                        class="object-cover w-full h-full min-w-full min-h-full"
                        :src="lounge?.featured_image?.src"
                    />
                </template>
                <template #preloader>
                    <div
                        class="object-cover w-full h-full min-w-full min-h-full shimmer bg-slate-200"
                    ></div>
                </template>
                <template #error>
                    <div
                        class="object-cover w-full h-full min-w-full min-h-full bg-slate-200 text-slate-400 flex items-center justify-center"
                    >
                        <Icon icon="no-image" />
                    </div>
                </template>
            </ImageLoader>
        </router-link>
        <div
            class="h-8 w-8 justify-center items-center flex absolute right-2.5 top-2.5"
        >
            <FavButton
                buttonClass="bg-white rounded-full"
                :isFavorite="isFavoriteLounge"
                @toggleFavorite="handleToggleFavorite"
            />
        </div>
        <router-link
            @click="() => setActiveLounge(lounge)"
            :to="{
                name: 'Lounge',
                params: {
                    id: lounge.id,
                },
            }"
            class="self-stretch flex-col mt-3 justify-start items-start gap-1 flex cursor-pointer focus:outline-none hover:text-slate-500 active:text-slate-500"
        >
            <div class="w-full flex-col justify-start items-start gap-1 flex">
                <!-- Name of lounge with tooltip -->
                <h3
                    class="self-stretch text-slate-800 font-medium text-left text-sm leading-5 m-0 relative group"
                >
                    <span
                        ref="nameRef"
                        class="block truncate max-w-full overflow-hidden whitespace-nowrap"
                    >
                        {{ lounge?.name.short }}
                    </span>
                    <!-- Tooltip -->
                    <span
                        v-if="showNameTooltip"
                        class="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity bg-slate-800 text-white text-sm rounded-lg py-1 px-2 w-auto bottom-full left-1/2 transform -translate-x-1/2 -translate-y-1 z-10 whitespace-nowrap"
                    >
                        {{ lounge?.name.short }}
                    </span>
                </h3>

                <!-- Location of lounge with tooltip -->
                <p
                    class="self-stretch text-slate-500 font-normal text-left text-sm leading-5 m-0 relative group"
                >
                    <span
                        ref="locationRef"
                        class="block truncate max-w-full overflow-hidden whitespace-nowrap"
                    >
                        {{ lounge?.location }}
                    </span>
                    <!-- Tooltip -->
                    <span
                        v-if="showLocationTooltip"
                        class="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity bg-slate-800 text-white text-sm rounded-lg py-1 px-2 w-auto bottom-full left-1/2 transform -translate-x-1/2 -translate-y-1 z-10 whitespace-nowrap"
                    >
                        {{ lounge?.location }}
                    </span>
                </p>
            </div>
        </router-link>
        <!-- START HOURS -->
        <div
            class="justify-start items-center gap-2 flex w-full mt-1"
            v-if="(lounge?.business_hours || lounge?.timing) && !hideHours"
        >
            <LoungeHours
                :hoursData="lounge?.business_hours || null"
                :isOpen="lounge?.open_now"
                :timing="lounge?.timing || null"
                buttonColor="text-slate-500"
            />
        </div>
        <!-- END HOURS -->
    </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue';
import { useLoungesStore } from '@/store/lounges';
import FavButton from '@/components/ui/fav-button/fav-button.vue';
import Icon from '@/components/ui/icon/icon.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import LoungeHours from '@/components/tools/lounges/partials/lounge-hours/lounge-hours.vue';
export default {
    name: 'LoungeCard',
    props: {
        lounge: {
            type: Object,
            required: true,
        },
        cardClass: {
            type: String,
            default:
                'flex-col justify-start items-start flex w-full sm:max-w-64 min-w-64 relative p-1',
        },
        hideHours: {
            type: Boolean,
            default: false,
        },
    },
    components: {
        FavButton,
        Icon,
        ImageLoader,
        LoungeHours,
    },
    setup(props) {
        const loungesStore = useLoungesStore();
        const { setActiveLounge, toggleFavorite } = loungesStore;
        const isFavoriteLounge = ref(props.lounge.favorite);
        const nameRef = ref(null);
        const locationRef = ref(null);
        const showNameTooltip = ref(false);
        const showLocationTooltip = ref(false);
        const handleToggleFavorite = async () => {
            isFavoriteLounge.value = !isFavoriteLounge.value;

            const res = await toggleFavorite(props.lounge, 'lounge');
            if (!res || res.status !== 200) {
                isFavoriteLounge.value = !isFavoriteLounge.value;
            }
        };

        const checkTruncation = (el) => {
            return (
                el.offsetWidth < el.scrollWidth ||
                el.offsetHeight < el.scrollHeight
            );
        };

        const updateTooltips = () => {
            if (nameRef.value) {
                showNameTooltip.value = checkTruncation(nameRef.value);
            }
            if (locationRef.value) {
                showLocationTooltip.value = checkTruncation(locationRef.value);
            }
        };

        onMounted(() => {
            updateTooltips();
            window.addEventListener('resize', updateTooltips);
        });

        watch(
            () => props.lounge,
            () => {
                updateTooltips();
            },
            { immediate: true }
        );
        return {
            handleToggleFavorite,
            isFavoriteLounge,
            setActiveLounge,
            nameRef,
            locationRef,
            showNameTooltip,
            showLocationTooltip,
        };
    },
};
</script>
